<template>
  <div class="ag-grid-date-range-filter">
    <label class="date-range-label">From</label>
    <div class="date-range-field">
      <b-form-datepicker
        v-model="from"
        class="date-range-input"
        size="sm"
        locale="en-US"
        placeholder="From date"
        reset-button
        :max="to || null"
        :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
      />
      <button
        type="button"
        class="date-range-clear"
        title="Clear the date this is read from"
        :disabled="!from"
        @click="clearFromDate">
        <BIconXCircle style="font-size: 1.2em;" class="bicon" />
      </button>
    </div>
    <label class="date-range-label">To</label>
    <div class="date-range-field">
      <b-form-datepicker
        v-model="to"
        class="date-range-input"
        size="sm"
        locale="en-US"
        placeholder="To date"
        reset-button
        :min="from || null"
        :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }" />
      <button
        type="button"
        class="date-range-clear"
        title="Clear the date this is read to"
        :disabled="!to"
        @click="clearToDate">
        <BIconXCircle style="font-size: 1.2em;" class="bicon" />
      </button>
    </div>
    <div class="date-range-actions">
      <el-button size="small" type="primary" :disabled="!dirty" @click="applySelection">Apply</el-button>
      <el-button size="small" :disabled="!anySelection" @click="clearSelection">Clear</el-button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import moment from 'moment'
import { BIconXCircle } from 'bootstrap-icons-vue'

/**
 * Column filter for a date column read over a span of dates. ag-grid's own date filter asks for a
 * span as one criterion, which it will not apply until both ends are filled in; here either end on
 * its own is a criterion of its own - everything from a date, or everything up to one - so the list
 * can be opened at a date and left open ended.
 *
 * The dates picked reach the API as the column's own params, one for each end, so a span is asked
 * for as both and one end as only that one.
 *
 * Configured through the column's filterParams:
 *   serverFiltered  Whether the query carries this column's criteria, in which case the rows the API
 *                   returned are all kept rather than filtered a second time here
 *   cellDateFormat  How the column reads its dates, for the views that compare them here
 */
export default defineComponent({
  name: 'AgGridDateRangeFilter',
  components: {
    BIconXCircle
  },
  data () {
    return {
      // What the boxes are showing, which reaches the grid on Apply and not before
      from: '',
      to: '',
      // The span the column is filtered by, which is what the query is built from
      applied: { from: '', to: '' },
      // Handed over by the grid when the popup goes up, and how it is put away again
      hidePopup: null
    }
  },

  computed: {
    /**
     * Whether the boxes hold a span the filter is not asking for yet, which is what there is to apply
     * @return {boolean}
     */
    dirty () {
      return this.from !== this.applied.from || this.to !== this.applied.to
    },
    /**
     * Whether there is anything to clear: what the boxes hold, or what the column is filtered by
     * with the boxes already emptied
     * @return {boolean}
     */
    anySelection () {
      return !!(this.from || this.to || this.applied.from || this.applied.to)
    }
  },

  methods: {
    /**
     * Hands the span the boxes hold to the grid and puts the popup away - the dates are picked, so
     * the user is done with it
     */
    applySelection () {
      if (!this.dirty) return this.closePopup()
      this.applied = { from: this.from, to: this.to }
      this.filterChanged()
      this.closePopup()
    },
    /**
     * Asks for the whole column back: the boxes are emptied, and so is what the column was filtered
     * by. It is the end of picking dates, so the popup is put away with it.
     */
    clearSelection () {
      if (!this.anySelection) return
      this.from = ''
      this.to = ''
      if (this.applied.from || this.applied.to) {
        this.applied = { from: '', to: '' }
        this.filterChanged()
      }
      this.closePopup()
    },
    /**
     * Empties the date the span is read from, leaving the one it is read to: the list is open at
     * that end again. Like picking a date, it is asked for on Apply and not before.
     */
    clearFromDate () {
      this.from = ''
    },
    // The same for the date the span is read to, leaving the one it is read from
    clearToDate () {
      this.to = ''
    },
    /**
     * Puts the filter popup away. Nothing else here does: the popup stays up while the dates are
     * being picked, so a span is one trip to the API.
     */
    closePopup () {
      if (this.hidePopup) {
        this.hidePopup()
        this.hidePopup = null
        return
      }
      if (this.params.api) this.params.api.hidePopupMenu()
    },
    filterChanged () {
      this.params.filterChangedCallback()
    },
    // The day out of a date the grid holds, which it writes with the time on it
    asDay (value) {
      if (!value) return ''
      const day = moment(String(value).split(' ')[0], 'YYYY-MM-DD', true)
      return day.isValid() ? day.format('YYYY-MM-DD') : ''
    },

    /* ag-grid IFilterComp */

    isFilterActive () {
      return !!(this.applied.from || this.applied.to)
    },
    /**
     * Where the query is built from the dates picked here, the API has already applied them to the
     * rows the grid was given and all of them stay. On the views that still filter client side the
     * column's own date is read against the span.
     */
    doesFilterPass (params) {
      if (this.params.serverFiltered) return true
      if (!this.isFilterActive()) return true
      const cellDate = moment(this.params.valueGetter(params.node), this.params.cellDateFormat || 'MM-DD-YYYY', true)
      if (!cellDate.isValid()) return false
      if (this.applied.from && cellDate.isBefore(moment(this.applied.from, 'YYYY-MM-DD'), 'day')) return false
      if (this.applied.to && cellDate.isAfter(moment(this.applied.to, 'YYYY-MM-DD'), 'day')) return false
      return true
    },
    /**
     * @return {Object|null} The ends of the span, under the names ag-grid's own date filter holds
     *                       them by, so a query built from either filter reads the same
     */
    getModel () {
      if (!this.isFilterActive()) return null
      return {
        filterType: 'date',
        type: 'inRange',
        dateFrom: this.applied.from || null,
        dateTo: this.applied.to || null
      }
    },
    setModel (model) {
      this.applied = {
        from: this.asDay(model && model.dateFrom),
        to: this.asDay(model && model.dateTo)
      }
      // The grid is saying what this filter is asking for, so the popup opens on it with nothing
      // left to apply
      this.from = this.applied.from
      this.to = this.applied.to
    },
    getModelAsString () {
      const { from, to } = this.applied
      if (from && to) return `${from} to ${to}`
      if (from) return `From ${from}`
      if (to) return `To ${to}`
      return ''
    },
    /**
     * ag-grid IFilterComp: the popup is up. It hands over the means of putting it away where it has
     * one to hand over, which Apply and Clear use.
     * @param params  { hidePopup }
     */
    afterGuiAttached (params) {
      this.hidePopup = (params && params.hidePopup) || null
    }
  }
})
</script>

<style scoped>
  .ag-grid-date-range-filter {
    padding: 8px;
    width: 240px;
  }
  .date-range-label {
    display: block;
    margin: 0 0 2px 0;
    color: #48576a;
    font-size: 12px;
  }
  .date-range-field {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }
  .date-range-input {
    flex: 1;
    min-width: 0;
  }
  .date-range-clear {
    flex: none;
    margin-left: 4px;
    padding: 0 2px;
    border: none;
    background: transparent;
    color: #a7a7a7;
    line-height: 1;
    cursor: pointer;
    margin-left: -20px;
    z-index: 1;
  }
  .date-range-clear:hover:not(:disabled) {
    color: #48576a;
  }
  /* Nothing to clear, so it stays in place rather than moving the boxes around as dates are picked */
  .date-range-clear:disabled {
    opacity: 0.35;
    cursor: default;
  }
  .date-range-actions {
    margin-top: 4px;
    text-align: right;
    border-top: 1px solid #e2e2e2;
    padding-top: 8px;
  }
</style>
